import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { PaywallCheckoutAttemptsProvider } from "@/react/components/Paywalls/Checkout/Attempts/PaywallCheckoutAttemptsProvider";
import { paywallCheckoutAttemptsModes } from "@/react/components/Paywalls/Checkout/Attempts/types";
import type {
  OnComplete,
  OnError,
  OnProgress,
} from "@/react/components/Paywalls/Checkout/Attempts/types";
import { useCheckoutSession } from "@/react/components/Paywalls/Checkout/contexts/CheckoutSessionContext";
import { resolveCheckoutErrorMessage } from "@/react/components/Paywalls/Checkout/helpers";
import { buildCompletedAttemptMoneyEvent } from "@/react/components/Paywalls/Checkout/helpers/metaMoneyEventHelpers";
import { getPaywallPriceAmount } from "@/react/components/Paywalls/Checkout/helpers/paywallPrices";
import type { CheckoutSessionPaywall } from "@/react/types/Paywalls/Checkout/Session";

import { reportCheckoutError } from "../../../Checkout/checkoutErrorHelpers";
import {
  firePurchaseForCompletedSteps,
  pushPurchaseEventFromOffer,
} from "../../helpers/ga4PurchaseHelper";
import { getCommunityRootUrl } from "../../helpers/RoutesHelper";
import { getMainStep } from "../../helpers/SessionStepQueryHelper";
import { useGoToNextStep } from "../../hooks/useGoToNextStep";
import { useOfferPageContext } from "./OfferPageProvider";

/**
 * AttemptsProvider for OfferPage (Upsells)
 *
 * Wraps upsell pages with PaywallCheckoutAttemptsProvider in express mode.
 * Express mode uses saved payment method from main checkout, skips address collection.
 *
 * This provider:
 * - Extracts data from CheckoutSession (community, member info)
 * - Uses OfferPageContext for paywall/price data
 * - Handles navigation to next step on completion
 */
export const AttemptsProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { setError } = useFormContext();
  const {
    session,
    isLoading: isSessionLoading,
    refetchSession,
    ga4FiredStepIdsRef,
  } = useCheckoutSession();
  const { paywall, stepId, previewMutation, getSelectedPrice } =
    useOfferPageContext();
  const { goToNextStep } = useGoToNextStep();

  // `session` is a query-cache object that gets a new reference on every
  // refetch. Reading it through a ref instead of putting it directly in
  // onComplete's deps keeps that callback's identity stable across refetches.
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Type assertion: OfferPage paywall includes member_payment_processor
  const offerPaywall = paywall as CheckoutSessionPaywall;

  const selectedPrice = getSelectedPrice();
  const preview = previewMutation.data;
  const orderAmount = getPaywallPriceAmount(selectedPrice, preview);
  const orderCurrency = paywall.currency.code.toLowerCase();
  const orderCurrencyMinimumChargeAmount = paywall.currency.min_charge_amount;
  const paywallPriceId = selectedPrice.id;

  // Community/Processor data from session
  const community = session?.community;
  const communityId = community?.id ?? 0;
  const communityProcessorId = community?.payment_processor.processor_id ?? "";
  const communityProcessorKey =
    community?.payment_processor.processor_key ?? "";
  const communityProcessorPaymentMethodConfigurationId =
    community?.payment_processor.payment_method_configuration_id ?? "";
  // Link not needed for express mode (saved payment method only)
  const doesCommunityProcessorSupportsLink = false;

  // Member data from session
  const communityMember = session?.community_member;
  const user = session?.user;
  const billingInfo = communityMember?.billing_info;
  const memberId = communityMember?.id;
  const memberUserId = user?.id;
  const memberEmail = user?.email;
  const memberName = communityMember?.name || user?.name;

  // Member payment processor: Get from main step's paywall since upsell paywalls may not have it
  // The session_client_secret is shared across all paywalls for the same community/member
  const mainStep = getMainStep(session?.steps ?? []);
  const mainPaywall = mainStep?.offers[0]?.paywall;
  const memberPaymentProcessor =
    offerPaywall.member_payment_processor ??
    mainPaywall?.member_payment_processor;
  const doesMemberHasDefaultPaymentMethod =
    memberPaymentProcessor?.is_member_with_saved_payment_methods ?? false;
  const memberProcessorCustomerSessionClientSecret =
    memberPaymentProcessor?.session_client_secret ?? undefined;

  // Member address from billing info (already collected in main checkout)
  const isMemberAddressComplete = billingInfo?.address_complete ?? false;
  const memberAddressCity = billingInfo?.address_city;
  const memberAddressCountry = billingInfo?.address_country;
  const memberAddressLine1 = billingInfo?.address_line1;
  const memberAddressLine2 = billingInfo?.address_line2 ?? undefined;
  const memberAddressPostalCode = billingInfo?.address_postal_code;
  const memberAddressState = billingInfo?.address_state;

  // Express mode: use saved payment method, no address collection
  const isPaymentMethodRequired = false;
  const isMemberAddressRequired = false;

  // Callback: payment completed successfully.
  //
  // Emit the Add-on purchase from attempt context to avoid waiting for the
  // webhook-driven step transition. After a successful session refresh, the
  // shared completed-step helper handles any other newly completed purchases.
  // The step ID is recorded in memory and sessionStorage for deduplication.
  const onComplete = useCallback<OnComplete>(
    async ({
      attemptId,
      attemptStatus,
      subscriptionId,
      paymentIntentId,
      invoiceId,
    }) => {
      let currentSession = sessionRef.current;
      const moneyEvent = buildCompletedAttemptMoneyEvent({
        attemptId,
        attemptStatus,
        currency: orderCurrency.toUpperCase(),
        currencySmallestUnit: paywall.currency.smallest_unit,
        hasConfiguredTrial: Boolean(selectedPrice.trial_enabled),
        value: selectedPrice.amount,
        upfrontPaymentAmount: selectedPrice.upfront_payment_amount ?? 0,
        installmentsCount: selectedPrice.installments_count ?? 0,
        priceType: selectedPrice.price_type,
        preview,
      });

      if (currentSession?.id) {
        pushPurchaseEventFromOffer({
          ga4Paywall: { id: paywall.id, display_name: paywall.display_name },
          selectedPrice,
          preview,
          subscriptionId,
          paymentIntentId,
          invoiceId,
          sessionId: currentSession.id,
          stepId,
          ga4FiredStepIdsRef,
        });
      }
      let didRefreshSession = false;
      try {
        currentSession = await refetchSession();
        didRefreshSession = true;
      } catch {
        // Analytics are best-effort; preserve checkout navigation on refresh errors.
      }
      if (didRefreshSession) {
        firePurchaseForCompletedSteps(currentSession, ga4FiredStepIdsRef);
      }
      goToNextStep(currentSession, stepId, moneyEvent);
    },
    [
      ga4FiredStepIdsRef,
      goToNextStep,
      refetchSession,
      orderAmount,
      orderCurrency,
      paywall.display_name,
      paywall.id,
      preview,
      selectedPrice,
      stepId,
    ],
  );

  // Callback: payment error - display error in form
  const paywallId = paywall?.id;
  const sessionStepId = stepId;
  const onError = useCallback<OnError>(
    ({ error }) => {
      reportCheckoutError(error, {
        caller: error.caller,
        communityId,
        extra: error.getExtra(),
        paywallId,
        paywallPriceId,
        sessionStepId,
      });
      setError("root", {
        message: resolveCheckoutErrorMessage(
          error,
          getCommunityRootUrl(session),
        ),
      });
    },
    [communityId, paywallId, paywallPriceId, session, sessionStepId, setError],
  );

  // Callback: payment in progress
  const onProgress = useCallback<OnProgress>(() => {
    // Progress is handled by the button via isProcessingAttempt from context
  }, []);

  // Wait for session to load before rendering - parent handles loading UI
  if (isSessionLoading) {
    return null;
  }

  return (
    <PaywallCheckoutAttemptsProvider
      // Community/Processor
      communityId={communityId}
      communityProcessorId={communityProcessorId}
      communityProcessorKey={communityProcessorKey}
      communityProcessorPaymentMethodConfigurationId={
        communityProcessorPaymentMethodConfigurationId
      }
      doesCommunityProcessorSupportsLink={doesCommunityProcessorSupportsLink}
      // Member
      doesMemberHasDefaultPaymentMethod={doesMemberHasDefaultPaymentMethod}
      isMemberAddressComplete={isMemberAddressComplete}
      memberAddressCity={memberAddressCity}
      memberAddressCountry={memberAddressCountry}
      memberAddressLine1={memberAddressLine1}
      memberAddressLine2={memberAddressLine2}
      memberAddressPostalCode={memberAddressPostalCode}
      memberAddressState={memberAddressState}
      memberEmail={memberEmail}
      memberId={memberId}
      memberName={memberName}
      memberProcessorCustomerSessionClientSecret={
        memberProcessorCustomerSessionClientSecret
      }
      memberUserId={memberUserId}
      // Order
      checkoutIntentToken={session?.checkout_intent_token}
      orderPriceType={selectedPrice.price_type}
      orderAmount={orderAmount}
      orderCurrency={orderCurrency}
      orderCurrencyMinimumChargeAmount={orderCurrencyMinimumChargeAmount}
      paywallId={paywall.id}
      paywallPriceId={paywallPriceId}
      sessionId={session?.id}
      sessionStepId={stepId}
      // Flags
      isMemberAddressRequired={isMemberAddressRequired}
      isPaymentMethodRequired={isPaymentMethodRequired}
      mode={paywallCheckoutAttemptsModes.express}
      // Callbacks
      onComplete={onComplete}
      onError={onError}
      onProgress={onProgress}
    >
      {children}
    </PaywallCheckoutAttemptsProvider>
  );
};
